import React, { useEffect, useState, useRef } from 'react'
import { createSelector } from 'reselect'
import { useSelector, useDispatch } from 'react-redux'
import {
  Dialog,
  Box,
  IconButton,
  List,
  ListItem,
  Divider,
  Button,
  CircularProgress,
  TextField,
  Snackbar,
} from '@material-ui/core'
import { Alert, AlertTitle } from '@material-ui/lab'
import { useSpring, animated } from 'react-spring'
import { makeStyles } from '@material-ui/core'
import { Scrollbars } from 'react-custom-scrollbars'
import CloseIcon from '@material-ui/icons/Close'
import RemoveIcon from '@material-ui/icons/Remove'
import AddIcon from '@material-ui/icons/Add'
import CurrencyFormat from 'react-currency-format'
import { LazyLoadImage } from 'react-lazy-load-image-component'

//COMPONENTS
import OrderGroup from '../OrderGroup/OrderGroup'

//API
import { getGroups } from '../../services/FirebaseServices/menuServices'

//REDUX
import {
  set_dialog_open,
  set_order_products,
  set_selected_product,
  makeSelectDialogOpen,
  makeSelectOrderProducts,
  makeSelectSelectedProduct,
  makeSelectRestaurantOpen,
} from '../../redux/ducks/restaurantMenu'
import { makeSelectSingleExperience } from '../../redux/ducks/experiences'

const stateSelector = createSelector(
  [
    makeSelectDialogOpen,
    makeSelectSelectedProduct,
    makeSelectOrderProducts,
    makeSelectRestaurantOpen,
    makeSelectSingleExperience,
  ],
  (
    isDialogOpen,
    selectedProduct,
    orderProducts,
    restaurantOpen,
    singleExperience,
  ) => ({
    isDialogOpen,
    selectedProduct,
    orderProducts,
    restaurantOpen,
    singleExperience,
  }),
)

const actionDispatch = (dispatch) => ({
  setDialogOpen: (isDialogOpen) => dispatch(set_dialog_open(isDialogOpen)),
  setOrder: (order) => dispatch(set_order_products(order)),
  setSelectedProduct: (selectedProduct) =>
    dispatch(set_selected_product(selectedProduct)),
})

const OrderDialog = ({ agencyId, experienceId, menuId }) => {
  //REDUX STATES
  const {
    isDialogOpen,
    selectedProduct,
    orderProducts,
    restaurantOpen,
    singleExperience,
  } = useSelector(stateSelector)
  const { setDialogOpen, setOrder, setSelectedProduct } = actionDispatch(
    useDispatch(),
  )

  const { name, description, key: productId, price } = selectedProduct

  //COMPONENT STATES
  const [groups, setGroups] = useState([])
  const [isLoading, setLoading] = useState(true)
  const [productTotalPrice, setProductTotalPrice] = useState(0)
  const [basePrice, setBasePrice] = useState(0)
  const [productAmount, setProductAmount] = useState(1)
  const [singleProductOrder, setSingleProductOrder] = useState({})
  const [observations, setObservations] = useState('')
  const [orderGroupAnimation, setOrderGroupAnimation] = useState(false)
  const [groupIsValid, setGroupIsValid] = useState([])
  const [snackBarOpen, setSnackBarOpen] = useState({
    isOpen: false,
    title: '',
    message: '',
  })

  const useStyles = makeStyles({
    paper: { height: '600px' },
  })
  const classes = useStyles()

  const { x } = useSpring({
    from: { x: 0 },
    x: orderGroupAnimation ? 1 : 0,
    config: { duration: 300 },
  })

  useEffect(() => {
    setLoading(true)
    setBasePrice(price)
    setProductTotalPrice(price * productAmount)

    const unsubscribeGroups = getGroups(
      agencyId,
      experienceId,
      menuId,
      productId,
    ).onSnapshot((querySnapshot) => {
      let data = []
      querySnapshot.forEach((doc) => {
        data.push(doc.data())
      })

      checkExistingProduct(data)
      setLoading(false)
    })
    return function cleanup() {
      unsubscribeGroups()
    }
  }, [productId])

  useEffect(() => {
    singleProductOrder.price && handleTotalPrice()
  }, [singleProductOrder])

  const checkExistingProduct = (groupsResponse) => {
    const productAdded = orderProducts.find(
      (orderProduct) => orderProduct.key === selectedProduct.key,
    )

    if (productAdded) {
      setProductAmount(productAdded.amount)
      setSingleProductOrder(productAdded)
      setObservations(productAdded.observations)
    } else {
      handleProductOrder(groupsResponse)
    }
    const filterGroups = groupsResponse.filter(
      (group) =>
        group.isDelete === false &&
        group.isActive === true &&
        group.options.length,
    )
    setGroups(filterGroups)

    //Se usa para la validacion de los grupos al momento
    //de agregar un producto a la orden
    const groupIsValidArray = filterGroups.map((item) => true)
    setGroupIsValid(groupIsValidArray)
  }

  const handleProductOrder = (groupsResponse) => {
    const { name, price, key } = selectedProduct
    const addGroups = groupsResponse.map((group) => ({
      name: group.name,
      key: group.key,
      isRequired: group.isRequired,
      options: [],
    }))

    const productInfo = {
      name,
      price,
      key,
      amount: 1,
    }

    setSingleProductOrder({ ...productInfo, groups: addGroups })
  }

  const handleClose = () => {
    setDialogOpen(false)
    setProductAmount(1)
    setProductTotalPrice(price)
    setObservations('')
    setSelectedProduct({})
  }

  const handleAddProduct = () => {
    const amount = productAmount + 1
    setProductAmount(amount)
    setProductTotalPrice(amount * basePrice)
  }

  const handleReduceProduct = () => {
    if (productAmount > 1) {
      const amount = productAmount - 1
      setProductAmount(amount)
      setProductTotalPrice(amount * basePrice)
    }
  }

  const handleTotalPrice = () => {
    const groupPrices = singleProductOrder.groups.map((group) => {
      if (group.options && group.options.length) {
        const pricesArray = group.options.map((option) =>
          option.isExtra ? option.price : 0,
        )
        const totalOption =
          pricesArray[0] && pricesArray.reduce((a, b) => a + b, 0)

        return totalOption
      } else {
        return 0
      }
    })

    const groupTotal = groupPrices.reduce((a, b) => a + b, 0)
    const basePrice = price + groupTotal

    setBasePrice(basePrice)
    setProductTotalPrice(basePrice * productAmount)
  }

  const validStock = () => {
    const startStock = selectedProduct.stock

    const endStock = startStock - productAmount

    if (endStock >= 0) {
      return true
    } else {
      return false
    }
  }

  const handleAddProductToShoppingCart = () => {
    if (singleExperience.category === 'ECOMMERCE') {
      const boolStock = validStock()
      if (!boolStock) {
        console.log('no es valido')
        setSnackBarOpen({
          isOpen: true,
          title: 'Lo sentimos',
          message:
            'Parece que el producto que desea agregar no se encuentra disponible en estos momentos.',
        })
        return
      }
    }

    const validRequiredGroups = singleProductOrder.groups.map((group) => {
      if (group.isRequired && !group.options.length) {
        return false
      } else {
        return true
      }
    })

    const checkIsValid = validRequiredGroups.every((item) => item === true)

    if (!restaurantOpen) {
      setSnackBarOpen({
        isOpen: true,
        title: 'En estos momentos estamos cerrados.',
        message:
          'Sin embargo, puedes ver nuestros productos y promociones; pero no podras realizar pedidos hasta que abramos nuevamente.',
      })

      return
    }

    if (checkIsValid) {
      const updateSingleProductoOrder = {
        ...singleProductOrder,
        price: productTotalPrice,
        amount: productAmount,
        basePrice,
        observations,
      }

      if (orderProducts.length) {
        const orderProductAddedIndex = orderProducts.findIndex(
          (orderProduct) => orderProduct.key === updateSingleProductoOrder.key,
        )
        //Si singleProductOrder no existe en orderProducts
        if (orderProductAddedIndex === -1) {
          setOrder([...orderProducts, updateSingleProductoOrder])
        } else {
          const orderUpdated = orderProducts.map((orderProduct) => {
            if (orderProduct.key === updateSingleProductoOrder.key) {
              return updateSingleProductoOrder
            } else {
              return orderProduct
            }
          })

          setOrder(orderUpdated)
        }
      } else {
        setOrder([...orderProducts, updateSingleProductoOrder])
      }

      handleClose()
    } else {
      setGroupIsValid(validRequiredGroups)
      setOrderGroupAnimation(!orderGroupAnimation)
    }
  }

  const handleSnackBarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return
    }

    setSnackBarOpen({ isOpen: false, title: '', message: '' })
  }

  return (
    <Dialog
      onClose={handleClose}
      open={isDialogOpen}
      fullWidth
      maxWidth="md"
      classes={{ paper: classes.paper }}
    >
      {isLoading ? (
        <Box display="flex" justifyContent="center" mt={5}>
          <CircularProgress />
        </Box>
      ) : (
        <Box display="flex" flex="1 1" height="100%" width="100%">
          <Box flex="1 1" width="50%">
            {selectedProduct.image && (
              <LazyLoadImage
                src={selectedProduct.image}
                width="auto"
                height="auto"
                alt={`${selectedProduct.name}`}
                style={{ width: '100%', height: '100%' }}
              />
            )}
          </Box>

          <Box display="flex" flex="1 1" flexDirection="column">
            <Box overflow="auto" flex="1 1" position="relative">
              <Scrollbars>
                <Box p={2}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <h2 style={{ marginTop: '0', marginBottom: '0' }}>
                      {name}
                    </h2>
                    <IconButton onClick={handleClose} size="small">
                      <CloseIcon />
                    </IconButton>
                  </Box>
                  {singleExperience.category === 'ECOMMERCE' &&
                    (selectedProduct.stock ? (
                      <small
                        style={{
                          color: '#999',
                        }}
                      >
                        Cantidad Disponible: {selectedProduct.stock} unidades
                      </small>
                    ) : (
                      <small
                        style={{
                          color: 'red',
                        }}
                      >
                        Cantidad Disponible: {selectedProduct.stock} unidades
                      </small>
                    ))}

                  <p style={{ color: '#999' }}>{description}</p>
                </Box>

                <Box>
                  <List dense>
                    {groups &&
                      groups.map((group, index) => {
                        return (
                          <Box key={group.key}>
                            {groupIsValid[index] ? (
                              <>
                                <Divider
                                  style={{ backgroundColor: '#673AB7' }}
                                />
                                <ListItem dense>
                                  <OrderGroup
                                    group={group}
                                    singleProductOrder={singleProductOrder}
                                    setSingleProductOrder={
                                      setSingleProductOrder
                                    }
                                  />
                                </ListItem>
                              </>
                            ) : (
                              <animated.div
                                style={{
                                  width: '100%',
                                  transform: x
                                    .to({
                                      range: [0, 0.2, 0.4, 0.6, 0.8, 1],
                                      output: [0, 20, 0, 20, 0],
                                    })
                                    .to((x) => `translate3d(${x}px, 0px, 0px)`),
                                }}
                              >
                                <Divider
                                  style={{ backgroundColor: '#673AB7' }}
                                />
                                <ListItem dense>
                                  <OrderGroup
                                    group={group}
                                    singleProductOrder={singleProductOrder}
                                    setSingleProductOrder={
                                      setSingleProductOrder
                                    }
                                  />
                                </ListItem>
                              </animated.div>
                            )}
                          </Box>
                        )
                      })}
                    <Divider style={{ backgroundColor: '#673AB7' }} />
                    <ListItem dense>
                      <Box width="100%">
                        <h4>Observaciones</h4>
                        <TextField
                          placeholder='Escribe cualquier comentario relacionado a tu pedido, ejemplo: "Sin vegetales"'
                          multiline
                          rows={4}
                          variant="outlined"
                          value={observations}
                          onChange={(event) =>
                            setObservations(event.target.value)
                          }
                        />
                      </Box>
                    </ListItem>
                  </List>
                </Box>
              </Scrollbars>
            </Box>
            <Box>
              <Box
                position="relative"
                zIndex="10"
                display="flex"
                padding="20px"
                boxShadow="0 0 35px rgb(50 50 93 / 10%), 0 -4px 15px rgb(0 0 0 / 7%)"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box display="flex" alignItems="center">
                  <IconButton color="primary" onClick={handleReduceProduct}>
                    <RemoveIcon />
                  </IconButton>
                  <Box
                    container="span"
                    style={{ fontSize: '1.3rem', fontWeight: 'bold' }}
                    paddingX={1}
                  >
                    {productAmount}
                  </Box>
                  <IconButton color="primary" onClick={handleAddProduct}>
                    <AddIcon />
                  </IconButton>
                </Box>
                <Box marginX={2} width="100%">
                  <Button
                    style={{
                      backgroundColor: '#3AB753',
                      color: 'white',
                      textTransform: 'capitalize',
                      width: '100%',
                    }}
                    onClick={handleAddProductToShoppingCart}
                  >
                    Agregar
                  </Button>
                </Box>
                <Box container="span" fontWeight="bold" fontSize="1.4rem">
                  <CurrencyFormat
                    value={productTotalPrice}
                    displayType="text"
                    thousandSeparator
                    prefix="$"
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
      <Snackbar
        open={snackBarOpen.isOpen}
        autoHideDuration={6000}
        onClose={handleSnackBarClose}
      >
        <Alert onClose={handleSnackBarClose} severity="error">
          <AlertTitle style={{ fontWeight: 'bold' }}>
            {snackBarOpen.title}
          </AlertTitle>
          {snackBarOpen.message}
        </Alert>
      </Snackbar>
    </Dialog>
  )
}

export default OrderDialog
